import * as RadPopover from '@radix-ui/react-popover'
import { useRef, useState } from 'react'
import { useDebounce, useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { ColorPicker, Input } from '@blade/ui-kit'
import { ReservedSpace } from '@root/shared/ui-kit/reserved-space/reserved-space'

const COLOR_DEBOUNCE_MS = 300
const HEX_COLOR_6 = /^[\dA-Fa-f]{6}$/
const HEX_COLOR_3 = /^[\dA-Fa-f]{3}$/

const expandShortHex = (short: string) =>
  [...short].map((char) => char + char).join('')

const toHexDraft = (value: string) =>
  value.startsWith('#') ? value.slice(1) : value

const parseHexColor = (raw: string): string | undefined => {
  const trimmed = raw.trim()
  if (HEX_COLOR_6.test(trimmed)) {
    return `#${trimmed}`
  }
  if (HEX_COLOR_3.test(trimmed)) {
    return `#${expandShortHex(trimmed)}`
  }
  return undefined
}

const parseHexColor6 = (raw: string): string | undefined => {
  const trimmed = raw.trim()
  if (HEX_COLOR_6.test(trimmed)) {
    return `#${trimmed}`
  }
  return undefined
}

type Props = {
  value: string | undefined
  previewColor: string
  onChange: (value: string) => void
}

export const ColorInput = ({ value, previewColor, onChange }: Props) => {
  const effectiveValue = value ?? previewColor
  const [localValue, setLocalValue] = useState(effectiveValue)
  const [hexDraft, setHexDraft] = useState(toHexDraft(effectiveValue))
  const lastEmitted = useRef(effectiveValue)

  useDebounce(
    () => {
      if (localValue !== lastEmitted.current) {
        lastEmitted.current = localValue
        onChange(localValue)
      }
    },
    COLOR_DEBOUNCE_MS,
    [localValue],
  )

  useUpdateEffect(() => {
    if (effectiveValue !== lastEmitted.current) {
      lastEmitted.current = effectiveValue
      setLocalValue(effectiveValue)
      setHexDraft(toHexDraft(effectiveValue))
    }
  }, [effectiveValue])

  const handleColorChange = (color: { hex: string }) => {
    setLocalValue(color.hex)
    setHexDraft(toHexDraft(color.hex))
  }

  const handleHexInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    setHexDraft(next)
    const parsed = parseHexColor6(next)
    if (parsed) {
      setLocalValue(parsed)
    }
  }

  const handleHexInputBlur = () => {
    const parsed = parseHexColor(hexDraft)
    if (parsed) {
      setLocalValue(parsed)
      setHexDraft(toHexDraft(parsed))
      return
    }
    setHexDraft(toHexDraft(localValue))
  }

  return (
    <PopoverRoot modal={false}>
      <PopoverTrigger asChild>
        <Swatch type="button" aria-label={localValue}>
          <ColorPreviewContainer>
            <ColorPreview $color={localValue} />
          </ColorPreviewContainer>
          <ColorHex>
            <ReservedSpace $chars={7}>{localValue}</ReservedSpace>
          </ColorHex>
        </Swatch>
      </PopoverTrigger>
      <RadPopover.Portal>
        <PopoverContent side="top" align="end" sideOffset={6}>
          <ColorPicker
            color={localValue}
            onChange={handleColorChange}
            onChangeComplete={handleColorChange}
          />
          <HexInput
            size="s"
            startContent="#"
            withStartSeparator
            type="text"
            inputMode="text"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            value={hexDraft}
            onChange={handleHexInputChange}
            onBlur={handleHexInputBlur}
            placeholder="000000"
          />
        </PopoverContent>
      </RadPopover.Portal>
    </PopoverRoot>
  )
}

const PopoverRoot = styled(RadPopover.Root)``

const PopoverTrigger = styled(RadPopover.Trigger)``

const PopoverContent = styled(RadPopover.Content)`
  width: 220px;
  padding: 4px;
  border: 1px solid var(--background-4);
  border-radius: 6px;
  background: var(--background-2);
  box-shadow: 0 -4px 20px var(--appearanceA-4);
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const HexInput = styled(Input)`
  width: 100%;
`

const Swatch = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: 6px;
  padding: 2px;
  background: var(--appearance-1);
`
const ColorPreviewContainer = styled.div`
  padding: 1px;
  border: 1px solid var(--background-4);
  border-radius: 6px;
`
const ColorPreview = styled.div.attrs<{ $color: string }>((x) => ({
  style: {
    backgroundColor: x.$color,
  },
}))`
  border-radius: 5px;
  width: 32px;
  height: 22px;
`
const ColorHex = styled.span`
  font-size: 12px;
  padding: 0 4px;
  font-weight: 500;
  color: var(--text-11);
  text-transform: uppercase;
`
